import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ReplyTemplateManagerService } from 'src/sb-tools/typescript/services/reply-template-manager.service';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AttachmentsModalComponent } from '../../modal-windows/attachments-modal/attachments-modal.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ReplyTemplatesModalComponent } from 'src/sb-modules/Frontend-Reply-Templates-Module/reply-templates-modal.component';
import { NewLinePipe } from 'src/sb-tools/typescript/pipes/new-line.pipe';
import { NoteGuiModel } from '@sb-tools/typescript/models/view/note-gui-model.model';
import Quill from 'quill';
import { Range } from 'quill/core/selection';
import { NoteDataService } from '../../../core/services/note-data.service';
import { NoteService } from '../../../core/services/note.service';
import { NoteQuillEditorComponent } from '../note-quill-editor/note-quill-editor.component';
import { FilePreviewComponent } from '../../../../sb-modules/Frontend-Document-Viewer-Module/components/file-preview/file-preview.component';
import { NgClass } from '@angular/common';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { InlineSVGComponent } from '../../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';

@Component({
    selector: 'app-new-note',
    templateUrl: './new-note.component.html',
    styleUrls: ['./new-note.component.scss'],
    imports: [
        NoteQuillEditorComponent,
        FilePreviewComponent,
        NgClass,
        TooltipModule,
        InlineSVGComponent,
        TranslateModule,
    ],
})
export class NewNoteComponent implements OnDestroy {
    @Input() isCancelAllowed?: boolean;
    @Input() isFocusNeeded?: boolean;
    @Input() isReplyNote?: boolean;
    @Input() newNote?: NoteGuiModel = {
        text: '',
        html: '',
        attachments: [],
    };
    @Input() noteId?: number = 0;
    @Input() parentNoteId?: number = null;
    @Input() reply?;
    @Input() ticketId: number;

    @Output() closeNoteEditor: EventEmitter<any> = new EventEmitter();
    @Output() noteAdd: EventEmitter<any> = new EventEmitter();

    isAdding = false;
    isInvalidMessage = true;
    quillEditor: Quill;
    quillRange: Range;

    private unsubscriber = new Subject<void>();

    constructor(
        public replyTemplateManagerService: ReplyTemplateManagerService,
        public noteService: NoteService,
        private bsModalService: BsModalService,
        private noteDataService: NoteDataService,
        private translateService: TranslateService,
        private newLinePipe: NewLinePipe,
    ) {}

    addNote() {
        if (this.isAdding) {
            return;
        }

        this.isAdding = true;

        if (this.newNote.html) {
            this.newNote.html = this.newNote.html.replaceAll('<div></div>', '<div><br></div>');
        }

        this.noteDataService
            .add(this.ticketId, {
                replyId: this.reply ? this.reply.id : null,
                parentNoteId: this.parentNoteId,
                message: {
                    text: this.newNote.text,
                    html: this.newNote.html,
                },
                mentionedUserIds: this.noteService.getMentionedIds('user', this.newNote.html) as string[],
                mentionedUserGroupIds: this.noteService.getMentionedIds('group', this.newNote.html) as number[],
                attachments: this.newNote.attachments,
                ticketId: this.ticketId,
            })
            .pipe(
                finalize(() => {
                    this.isAdding = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe((newNote) => {
                this.noteAdd.emit(newNote);
                this.newNote.html = '';
                this.newNote.text = '';
                this.newNote.attachments.length = 0;

                if (this.reply) {
                    this.reply.noteIds.push(newNote.id);
                }
            });
    }

    cancel() {
        this.closeNoteEditor.emit();
    }

    openAttachmentsModal() {
        const initialState = {
            newAttachments: this.newNote.attachments,
            existedAttachmentsLength: this.newNote.attachments.length,
        };

        this.bsModalService.show(AttachmentsModalComponent, {
            initialState,
            ignoreBackdropClick: true,
        });
    }

    openReplyTemplatesModal($event) {
        this.updateQuillSelection();

        const initialState = {
            isTextContent: false,
            confirmCallback: (templateText, attachments) => {
                return this.replyTemplateManagerService.chooseReplyTemplate({
                    $event,
                    templateText: this.newLinePipe.transform(templateText),
                    quillEditor: this.quillEditor,
                    quillRange: this.quillRange,
                    reply: this.newNote,
                    attachments,
                });
            },
        };

        this.bsModalService.show(ReplyTemplatesModalComponent, {
            initialState,
        });
    }

    updateQuillSelection() {
        this.quillRange = this.replyTemplateManagerService.updateRange(this.quillEditor);
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
